import type { SkillData } from '../spacetime/modules/SkillModule';
import type { AppearanceCatalogEntry, ItemCatalogEntry, MobCatalogEntry } from '../spacetime/types';
import { t } from '../i18n';
import { localizeItemName, localizeSkillDescription, localizeSkillName } from '../i18n/content';
import { createMobSpriteIcon, type CodexTab } from './codexShared';
import type { CodexListRow } from './codexTabRenderer';

export const CODEX_CHAT_INSERT_EVENT = 'codex:insert-chat-link';
const CODEX_TOKEN_SUFFIX = ']]';
const SHORT_CODE_BY_KIND: Record<CodexTab, string> = {
  skills: 'skills',
  items: 'items',
  mobs: 'mobs',
  appearances: 'appearances',
};
const KIND_BY_SHORT_CODE: Record<string, CodexTab> = {
  skills: 'skills',
  items: 'items',
  mobs: 'mobs',
  appearances: 'appearances',
};
const CODEX_TOKEN_PATTERN = /\[\[(skills|items|mobs|appearances):([^\]]+)\]\]|\[\[codex:(skills|items|mobs|appearances):([^:\]]+):([^\]]*)\]\]/g;

export interface CodexChatLinkPayload {
  kind: CodexTab;
  value: string;
  label: string;
}

export interface CodexTooltipResolverData {
  skills: SkillData[];
  items: ItemCatalogEntry[];
  mobs: MobCatalogEntry[];
  appearances: AppearanceCatalogEntry[];
}

export type CodexMessagePart =
  | { kind: 'text'; text: string }
  | { kind: 'link'; payload: CodexChatLinkPayload };

export function buildCodexChatToken(payload: CodexChatLinkPayload): string {
  const encodedValue = encodeURIComponent(payload.value);
  const encodedLabel = encodeURIComponent(payload.label);
  return `[[codex:${SHORT_CODE_BY_KIND[payload.kind]}:${encodedValue}:${encodedLabel}${CODEX_TOKEN_SUFFIX}`;
}

export function dispatchCodexChatInsert(payload: CodexChatLinkPayload): void {
  window.dispatchEvent(new CustomEvent<CodexChatLinkPayload>(CODEX_CHAT_INSERT_EVENT, { detail: payload }));
}

export function parseCodexMessageParts(text: string): CodexMessagePart[] {
  const parts: CodexMessagePart[] = [];
  let lastIndex = 0;
  for (const match of text.matchAll(CODEX_TOKEN_PATTERN)) {
    const index = match.index ?? 0;
    if (index > lastIndex) {
      parts.push({ kind: 'text', text: text.slice(lastIndex, index) });
    }
    parts.push({
      kind: 'link',
      payload: {
        kind: match[1]
          ? KIND_BY_SHORT_CODE[match[1]] ?? 'items'
          : (match[3] as CodexTab),
        value: decodeURIComponent((match[2] ?? match[4] ?? '').trim()),
        label: decodeURIComponent((match[5] ?? '').trim()),
      },
    });
    lastIndex = index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push({ kind: 'text', text: text.slice(lastIndex) });
  }
  if (parts.length === 0) {
    parts.push({ kind: 'text', text });
  }
  return parts;
}

export function resolveCodexPreviewRow(
  payload: CodexChatLinkPayload,
  data: CodexTooltipResolverData
): CodexListRow | null {
  if (payload.kind === 'skills') {
    const skill = data.skills.find((entry) => entry.skillId === payload.value);
    if (!skill) {
      return {
        value: payload.value,
        icon: '❓',
        title: payload.label || payload.value,
      };
    }
    const stats = [
      skill.damage > 0 ? t('character.statDmg', { value: skill.damage }) : '',
      skill.range > 0 ? t('character.statRange', { value: skill.range }) : '',
      skill.durationTicks > 0 ? t('character.statDuration', { value: skill.durationTicks }) : '',
      skill.cooldownTicks > 0 ? t('character.statCooldown', { value: skill.cooldownTicks }) : '',
      skill.staminaCost > 0 ? `Stamina ${skill.staminaCost}` : '',
    ].filter(Boolean).join(' · ');
    return {
      value: skill.skillId,
      icon: skill.emoji || '📦',
      title: localizeSkillName(skill.skillId, skill.displayName),
      idText: skill.skillId,
      description: localizeSkillDescription(skill.skillId, skill.description),
      stats,
      tags: skill.tags.length > 0 ? skill.tags : undefined,
    };
  }

  if (payload.kind === 'items') {
    const item = data.items.find((entry) => entry.itemId === payload.value);
    if (!item) {
      return {
        value: payload.value,
        icon: '❓',
        title: payload.label || payload.value,
      };
    }
    const emoji = typeof item.metadata.emoji === 'string' && item.metadata.emoji.trim().length > 0 ? item.metadata.emoji : '📦';
    return {
      value: item.itemId,
      icon: emoji,
      title: localizeItemName(item.itemId, item.metadata, item.displayName),
      idText: item.itemId,
      stats: `Type: ${item.itemType} · Max Stack: ${item.maxStack}`,
    };
  }

  if (payload.kind === 'mobs') {
    const mob = data.mobs.find((entry) => String(entry.mobTypeId) === payload.value);
    if (!mob) {
      return {
        value: payload.value,
        icon: '❓',
        title: payload.label || payload.value,
      };
    }
    return {
      value: String(mob.mobTypeId),
      icon: createMobSpriteIcon(mob.entityType),
      title: mob.displayName,
      idText: String(mob.mobTypeId),
      stats: `Type: ${mob.entityType} · HP: ${mob.baseHp} · Mana: ${mob.baseMana} · Move: ${mob.moveDistance} · Speed: ${mob.speed}`,
    };
  }

  const appearance = data.appearances.find((entry) => entry.entityType === payload.value);
  if (!appearance) {
    return {
      value: payload.value,
      icon: '❓',
      title: payload.label || payload.value,
    };
  }
  return {
    value: appearance.entityType,
    icon: createMobSpriteIcon(appearance.entityType),
    title: appearance.displayName,
    idText: appearance.entityType,
    stats: `Appearance: ${appearance.entityType}`,
  };
}
